import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { components } from "@/types/api.generated";
import type { Objective, ObjectiveStatus, ObjectiveType, ObjectiveLevel } from "@/types/entities";
import { toast } from "sonner";

export interface ObjectiveFilters {
  owner_id?: string;
  year?: number;
  level?: ObjectiveLevel;
  status?: ObjectiveStatus;
  type?: ObjectiveType;
  parent_objective_id?: string;
  focus_area_id?: string;
  team_of?: string;
  cursor?: string;
  limit?: number;
}

export function useObjectives(filters: ObjectiveFilters = {}) {
  return useQuery({
    queryKey: ["objectives", filters],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/objectives", {
        params: { query: filters },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
  });
}

export function useObjective(id: string) {
  return useQuery({
    queryKey: ["objectives", id],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/objectives/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateObjective() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CreateObjectiveRequest"]) => {
      const { data, error } = await apiClient.POST("/v1/objectives", { body });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["objectives"] });
      toast.success("Objective created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateObjective(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["UpdateObjectiveRequest"]) => {
      const { data, error } = await apiClient.PUT("/v1/objectives/{id}", {
        params: { path: { id } },
        body,
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onMutate: async (newData) => {
      await queryClient.cancelQueries({ queryKey: ["objectives", id] });
      const previous = queryClient.getQueryData<{ data: Objective }>(["objectives", id]);
      if (previous) {
        queryClient.setQueryData(["objectives", id], {
          ...previous,
          data: { ...previous.data, ...newData },
        });
      }
      return { previous };
    },
    onError: (_error: Error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["objectives", id], context.previous);
      }
      toast.error("Failed to save changes");
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["objectives"] });
    },
  });
}

export function useDeleteObjective() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/objectives/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["objectives"] });
      toast.success("Objective deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateObjectiveStatus(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (status: ObjectiveStatus) => {
      const { data, error } = await apiClient.PATCH("/v1/objectives/{id}/status", {
        params: { path: { id } },
        body: { status },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["objectives"] });
      toast.success("Status updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
